import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PublishedEvent } from '../../@types';
import { QUERY_KEYS, SECOND, USED_FOR } from '../../constants';
import { getData } from '../../utils/api';
import useLocalization from '../useLocalization';
import { useMarketGroups, usePublishedMarkets } from '../../queries';
import { useMarketGroupNavigation } from '../useMarketGroupNavigation';

export const useEventPage = () => {
  const { eventId, marketGroupId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();

  const {
    data: publishedEvent,
    isError,
    isPending: isEventLoading,
  } = useQuery({
    queryKey: [QUERY_KEYS.publishedEvents, eventId],
    queryFn: (): Promise<{ item: PublishedEvent }> => getData(`events/${eventId}/published`),
    enabled: !!eventId,
    select: (data) => data.item,
    refetchInterval: 30 * SECOND,
  });

  const sportId = publishedEvent?.tournament?.sport?.id;

  const { data: marketGroups, isPending: areMarketGroupsLoading } = useMarketGroups(sportId, {
    usedFor: [USED_FOR.PRE_MATCH],
  });

  const {
    markets,
    isFetchingNextPage,
    hasNextPage,
    lastElementRef,
    isLoading: areMarketsLoading,
    isFetching: areMarketsFetching,
  } = usePublishedMarkets({
    eventId,
    marketGroupId,
    isLive: false,
  });

  useMarketGroupNavigation({ marketGroupId, marketGroups });

  const event = useMemo(() => {
    if (!publishedEvent) return undefined;

    return {
      ...publishedEvent,
      markets: publishedEvent.isLive ? [] : markets,
    };
  }, [publishedEvent, markets]);

  const handleActiveGroupChange = useCallback(
    (groupId: string | undefined) => {
      navigate(`../${groupId}`, { replace: true });
    },
    [navigate]
  );

  const areInitialMarketsLoading =
    isEventLoading || (Boolean(sportId) && areMarketGroupsLoading) || !marketGroupId || areMarketsLoading;

  return {
    eventId,
    marketGroupId,
    publishedEvent,
    event,
    isError,
    isEventLoading,
    areInitialMarketsLoading,
    areMarketGroupsLoading,
    areMarketsLoading,
    areMarketsFetching,
    isFetchingNextPage,
    hasNextPage,
    lastElementRef,
    marketGroups: marketGroups ?? [],
    handleActiveGroupChange,
    t,
  };
};
